import type {
  ISeptimaSearchResultItem,
  ISeptimaSearchResultType,
} from "../../components/SeptimaSearchReact/interfaces/types";
import type { IVirksomhed } from "../../components/wizards/OpretFirma/opretFirmaInterface";
import type { IProduktionsenhed } from "../../interfaces/firma";
import type { CvrSearchHitDto, CvrSearchProduktionsenhedDto } from "./types";

const CVR_RESULT_TYPE: ISeptimaSearchResultType = {
  id: "virksomhed",
  singular: "Virksomhed",
  plural: "Virksomheder",
  description: "",
  geometrySupport: "",
  iconURI: "",
  queryBehaviour: "",
};

const defaultGeometri = {
  type: "Point" as const,
  coordinates: [0, 0] as [number, number],
  crs: { type: "", properties: { name: "" } },
};

function mapProduktionsenhed(
  dto: CvrSearchProduktionsenhedDto,
  cvrNr: number,
): IProduktionsenhed {
  const adresse =
    dto.adresse ??
    (dto as unknown as { adgangsadressebetegnelse?: string })
      .adgangsadressebetegnelse ??
    "";
  return {
    adgangsadressebetegnelse: adresse,
    adresse,
    adresseid: dto.adresseid ?? "",
    branche: "",
    branchekode: "",
    cvrnummer: cvrNr,
    er_aktiv: dto.er_aktiv ?? true,
    geometri: { type: "Point", coordinates: [0, 0], crs: {} },
    gruppekode: "",
    hovedafdelingkode: "",
    hovedgruppekode: "",
    husnummerid: "",
    kommunekode: "",
    navn: dto.navn ?? "",
    pnummer: dto.pNr,
    reklamebeskyttet: "False",
    sammensatstatus: dto.sammensatstatus ?? "Aktiv",
    septima_antal_ansatte_seneste: 0,
    septima_email: "",
    septima_search_document: "",
    septima_search_document_adgangsadressebetegnelse: "",
    septima_search_document_fornavn: "",
    septima_search_document_navn: "",
    septima_tlf: "",
    undergruppekode: "",
  };
}

function mapHitToVirksomhed(hit: CvrSearchHitDto): IVirksomhed {
  const adresse = mapBeliggenhedsadresseToString(hit.beliggenhedsadresse);
  const produktionsenheder: IProduktionsenhed[] = hit.produktionsenheder?.map(
    (pe) => mapProduktionsenhed(pe, hit.cvrNummer),
  ) ?? [
    mapProduktionsenhed(
      {
        pNr: 0,
        navn: hit.virksomhedsnavn ?? "",
        adresse,
      },
      hit.cvrNummer,
    ),
  ];

  return {
    cvrnummer: hit.cvrNummer,
    navn: hit.virksomhedsnavn ?? "",
    adresse: adresse,
    branchekode: "",
    branche: "",
    sammensatstatus: "Aktiv",
    adresseid: hit.beliggenhedsadresse?.darAdresseId ?? "",
    geometri: defaultGeometri,
    produktionsEnheder: produktionsenheder,
  };
}

function mapBeliggenhedsadresseToString(
  beliggenhedsadresse: CvrSearchHitDto["beliggenhedsadresse"],
): string {
  if (!beliggenhedsadresse) return "";

  return `${beliggenhedsadresse.vejnavn} ${beliggenhedsadresse.husnummerFra ?? ""}${beliggenhedsadresse.bogstavFra ?? ""} - ${beliggenhedsadresse.husnummerTil ?? ""}${beliggenhedsadresse.bogstavTil ?? ""}`;
}

export function mapCvrSearchHitsToSeptimaResults(
  hits: CvrSearchHitDto[],
): ISeptimaSearchResultItem[] {
  return hits.map((hit, index): ISeptimaSearchResultItem => {
    const data = mapHitToVirksomhed(hit);
    const title = data.navn || String(data.cvrnummer);
    return {
      id: hit.cvrNummer ?? index,
      data,
      image: "",
      newquery: title,
      source: "cvr",
      title,
      type: CVR_RESULT_TYPE,
      isComplete: true,
    };
  });
}
